import 'dayjs/locale/en';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/en-in';
import 'dayjs/locale/hi';
import 'dayjs/locale/pt';
import 'dayjs/locale/es';
import 'dayjs/locale/ja';
import dayjs, { Dayjs } from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { getUserLocale } from '.';

dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

enum DateTemplates {
  // Base localized formats
  timeShort = 'LT', // h:mm A (8:02 PM)
  timeLong = 'LTS', // h:mm:ss A (8:02:18 PM)
  short = 'L', // MM/DD/YYYY (08/16/2018)
  medium = 'LL', // MMMM D, YYYY (August 16, 2018)
  long = 'LLL', // MMMM D, YYYY h:mm A (August 16, 2018 8:02 PM)
  full = 'LLLL', // dddd, MMMM D, YYYY h:mm A (Thursday, August 16, 2018 8:02 PM)
  shortThin = 'l', // M/D/YYYY (8/16/2018)
  mediumThin = 'll', // MMM D, YYYY (Aug 16, 2018)
  longThin = 'lll', // MMM D, YYYY h:mm A (Aug 16, 2018 8:02 PM)
  fullThin = 'llll', // ddd, MMM D, YYYY h:mm A (Thu, Aug 16, 2018 8:02 PM)
  // Combined formats
  isoDate = 'YYYY-MM-DD', // ISO date format (2024-01-15)
  dateTimeShort = 'L LT', // MM/DD/YYYY h:mm A (08/16/2018 8:02 PM)
  dateTimeLong = 'LLL LTS', // MMMM D, YYYY h:mm:ss A (August 16, 2018 8:02:18 PM)
  monthYear = 'MMMM YYYY', // Month and year only (January 2024)
  monthDayTime = 'MMM D LT', // MMM D h:mm A (Aug 16, 2018 8:02 PM)
}

type DateTemplateKeys = keyof typeof DateTemplates;

export interface FormatDateOptions {
  template?: DateTemplateKeys;
  format?: string; // Custom format string (takes precedence over template)
  utc?: boolean; // Convert to UTC, default is true
  locale?: string; // Locale to override user locale
}

export const formatDate = (
  date: string | Date | Dayjs,
  options: FormatDateOptions = {},
) => {
  const locale = getDayjsLocale(options.locale || getUserLocale());
  // Use custom format if provided, otherwise use template
  const formatString =
    options.format || DateTemplates[options.template || 'short'];

  return dayjs(date)
    .utc(options.utc ?? true)
    .locale(locale)
    .format(formatString);
};

/**
 * Format a date as expiration date: date-only in UTC so the calendar day
 * is the same for all users regardless of timezone/locale. Use for account/credit
 */
export const formatExpirationDate = (
  date: string | Date | Dayjs | null | undefined,
): string => {
  if (date == null) return '-';
  const parsed = dayjs.utc(date);
  if (!parsed.isValid()) return '-';
  const locale = getDayjsLocale(getUserLocale());
  return parsed.locale(locale).format(DateTemplates.short);
};

const getDayjsLocale = (locale: string) => {
  const normalized = locale.toLowerCase();
  const [lang, region] = normalized.split(/[-_]/);

  if (lang === 'en' && region === 'us') return 'en';
  if (lang === 'hi') return 'hi';
  if (region === 'in') return 'en-in';
  if (lang === 'es') return 'es';
  if (lang === 'pt') return 'pt';
  if (lang === 'ja') return 'ja';

  return 'en-gb';
};

export interface FormatDurationOptions {
  locale?: string; // Locale to override user locale
}

export const formatDuration = (
  value: number,
  unit:
    | 'milliseconds'
    | 'seconds'
    | 'minutes'
    | 'hours'
    | 'days'
    | 'weeks'
    | 'months'
    | 'years',
  options: FormatDurationOptions = {},
) => {
  const locale = getDayjsLocale(options.locale || getUserLocale());

  return dayjs.duration(value, unit).locale(locale).humanize();
};

// Mapping of weekday names to dayjs day indices
const WEEKDAY_MAP: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

/**
 * Get the start and end dates of the current week period
 * @param weekStartDay - Name of the day the week starts (e.g., "Monday", "Tuesday")
 * @param tz - Timezone to use for calculations (optional)
 * @returns Object with startDate and endDate as Dayjs objects
 */
export const getWeekPeriodDates = (
  weekStartDay: string,
  tz?: string,
): { startDate: Dayjs; endDate: Dayjs } => {
  const startDayIndex = WEEKDAY_MAP[weekStartDay];
  if (startDayIndex === undefined) {
    throw new Error(`Invalid weekday name: ${weekStartDay}`);
  }

  // Get current date in the specified timezone or local
  const now = tz ? dayjs().tz(tz) : dayjs();
  const currentDayIndex = now.day();

  // Calculate how many days ago the week started
  let daysFromWeekStart = currentDayIndex - startDayIndex;
  if (daysFromWeekStart < 0) {
    daysFromWeekStart += 7;
  }

  // Calculate week start and end dates
  const startDate = now.subtract(daysFromWeekStart, 'day').startOf('day');
  const endDate = startDate.add(6, 'day').endOf('day');

  return { startDate, endDate };
};

/**
 * Calculate the current week period based on a specific start day
 * @param weekStartDay - Name of the day the week starts (e.g., "Monday", "Tuesday")
 * @param tz - Timezone to use for calculations (optional)
 * @returns Formatted string representing the week period (e.g., "21 - 28 Oct 2025")
 */
export const calculateWeekPeriod = (
  weekStartDay: string,
  tz?: string,
): string => {
  const { startDate, endDate } = getWeekPeriodDates(weekStartDay, tz);

  // Format the period string
  const startFormatted = startDate.format('D');
  const endFormatted = endDate.format('D MMM YYYY');

  return `${startFormatted} - ${endFormatted}`;
};

/**
 * Check if a date string falls within a date range
 * @param dateStr - Date string to check
 * @param startDate - Start of the range (Dayjs object)
 * @param endDate - End of the range (Dayjs object)
 * @returns Boolean indicating if the date is within the range (inclusive)
 */
export const isDateInRange = (
  dateStr: string,
  startDate: Dayjs,
  endDate: Dayjs,
): boolean => {
  if (!dateStr) {
    return false;
  }

  const date = dayjs(dateStr);
  if (!date.isValid()) {
    return false;
  }

  return date.isSameOrAfter(startDate) && date.isSameOrBefore(endDate);
};

export function toDayjsOrNull(value: unknown): Dayjs | null {
  if (value == null) return null;
  if (dayjs.isDayjs(value)) return value;
  if (value instanceof Date) return dayjs(value);
  if (typeof value === 'string' || typeof value === 'number')
    return dayjs(value);
  return null;
}
